// The page text's two effects, which share the words' styles:
//
// - The waves (`setWaves()`): on a crest, words get heavy and casual; in the trough behind it,
//   they get light. Overlapping waves add up. The fronts (`gustOffset`) and their shape
//   (`waveShape` in waves/waves.ts) are the same ones the background glyphs use.
// - The dissolve (`setOutline()`): as text scrolls up under the stopped portrait, each word
//   within `DISSOLVE_DISTANCE` of the face's outline above it thins to Recursive's lightest
//   weight, turns casual, drifts up and to the side, and fades, gone by the time it meets the
//   outline. Underlines, rules, and images fade with it, so nothing slides under the face.
//   The about pages' pictures dissolve into dots instead (halftone-layer.ts), and only fade
//   with reduced motion. Scrolling back reverses it all. Anything fully faded takes no clicks.
//
// Only Recursive's `wght` and `CASL` axes move, and Recursive keeps every width at every weight
// and casualness, so nothing reflows. The drift uses relative offsets, which don't move
// anything else either.

import { gustOffset } from "@bfly/engine";
import { clamp01, smoothstep } from "@bfly/engine/math";
import type { PortraitOutline } from "../portrait/face-outline.js";
import { waveShape, type WaveRing } from "../waves/waves.js";
import { AGE_BAND, ageAt, channelOf, type WaveAges } from "../waves/wave-ages.js";
import { motionReduced, onMotionChange } from "../page/motion.js";
import { onThemeChange } from "../page/theme.js";
import { HalftoneLayer } from "./halftone-layer.js";
import { pageScrollX, pageScrollY } from "../page/page-scroll.js";

// At full height, the crest adds this much weight and the trough takes this much away.
const TEXT_CREST_WEIGHT = 380;
const TEXT_TROUGH_WEIGHT = 120;
// Recursive's weight range.
const TEXT_WEIGHT_MIN = 300;
const TEXT_WEIGHT_MAX = 1000;
// The dissolve starts this far below the face's outline, in CSS pixels, or at an element's
// resting distance if that's smaller, so nothing dissolves before the portrait stops. Over the
// first `DISSOLVE_THIN` of it (a fraction), a word thins and turns casual; from `DISSOLVE_FADE` on,
// it fades.
export const DISSOLVE_DISTANCE = 30; // matches `$portrait-fade` in _tokens.scss
const DISSOLVE_THIN = 0.6;
const DISSOLVE_FADE = 0.4;
// How far a dissolving word drifts up and to the side by the time it reaches the outline, in
// CSS pixels.
const DISSOLVE_RISE = 14;
const DISSOLVE_SCATTER = 6;
// Wave heights and dissolve progress snap to this step, so an element only gets restyled when
// its look actually changes.
const TEXT_STEP = 0.05;

// The property that turns off a faded element's clicks: its own `pointer-events`, or the custom
// property that a card's whole-card link target reads.
type ClickProperty = "pointer-events" | "--card-target";

// Things other than words that fade with the dissolve, and which part of them fades. `edge` is
// the side whose distance to the outline counts (where the faded line is). Once an element has
// fully faded, the property its rule names in `clicks` becomes `none`, so a hidden link or
// phrase under the face takes no clicks. A rule with null keeps its clicks. A `reducedOnly`
// fade only applies with reduced motion; with motion on, the halftone layer dissolves those
// pictures instead (halftone-layer.ts).
interface DecorRule {
  selector: string;
  property: string;
  edge: "top" | "bottom";
  clicks: ClickProperty | null;
  reducedOnly?: boolean;
}
const DISSOLVE_DECOR: readonly DecorRule[] = [
  // A link's underline, and an abbreviation's dotted one (About me's age).
  {
    selector: "a, abbr[title]",
    property: "text-decoration-color",
    edge: "bottom",
    clicks: "pointer-events",
  },
  {
    selector: ".slot[role='button']",
    property: "border-bottom-color",
    edge: "bottom",
    clicks: "pointer-events",
  },
  // A card that reaches under the face loses its whole-card target (the title link's `::after`
  // reads `--card-target`). Its title keeps its own clicks while it's visible.
  { selector: ".projects li", property: "--divider", edge: "top", clicks: "--card-target" },
  // A label's row of glyphs (the `label-rule` mixin in _tokens.scss).
  { selector: "h2, .about h3", property: "--label-rule", edge: "top", clicks: null },
  // A shelf label's mark (_about.scss).
  { selector: ".shelf-label", property: "--label-mark", edge: "top", clicks: null },
  // A list's bullets, its rows' rules, a nested list's guide, and the line between two lists
  // side by side (_about.scss).
  { selector: ".list li", property: "--list-mark", edge: "top", clicks: null },
  { selector: ".list > li", property: "--list-rule", edge: "top", clicks: null },
  { selector: ".list ul", property: "--list-guide", edge: "top", clicks: null },
  { selector: ".list-columns > * + *", property: "--divider", edge: "top", clicks: null },
  { selector: "img:not(.about img)", property: "opacity", edge: "top", clicks: "pointer-events" },
  { selector: "svg", property: "opacity", edge: "top", clicks: "pointer-events" },
  // The inverted labels (the projects' titles, home's "What else?" link and the link to the
  // 2026 version, and the notes' labels), so no dark band shows through the face.
  {
    selector: ".projects h3 a, .cta a, .note-label",
    property: "background-color",
    edge: "top",
    clicks: "pointer-events",
  },
  // The about pages' pictures fade with reduced motion. With motion, they dissolve into dots
  // starting at the outline, and a fade before that would show first.
  {
    selector: ".about img:not(.shelf img)",
    property: "opacity",
    edge: "top",
    clicks: "pointer-events",
    reducedOnly: true,
  },
  // A shelf tile is a link: it fades as a whole, icon included, and takes no clicks once it's
  // gone.
  {
    selector: ".shelf a",
    property: "opacity",
    edge: "top",
    clicks: "pointer-events",
    reducedOnly: true,
  },
  {
    selector: "jrsm-video video",
    property: "opacity",
    edge: "top",
    clicks: "pointer-events",
    reducedOnly: true,
  },
  // A player's rail fades by its own position, like text.
  { selector: ".video-rail", property: "opacity", edge: "top", clicks: "pointer-events" },
  // With reduced motion, this is what makes a player pause its video under the face
  // (jrsm-video.ts); with motion, the halftone layer's mark does it. The player keeps its
  // clicks, since its rail and its video have their own.
  { selector: "jrsm-video", property: "--dissolve", edge: "top", clicks: null, reducedOnly: true },
];

/** A word the effects restyle, with its box in page coordinates (CSS pixels) and its resting
    look. */
interface Word {
  element: HTMLSpanElement;
  left: number;
  right: number;
  top: number;
  middle: number;
  weight: number;
  casual: number;
  /** Which way the word drifts to the side, from -1 to 1. */
  aside: number;
  /** The current wave height, from -1 to 1. */
  height: number;
  /** The current dissolve progress, from 0 to 1. */
  dissolve: number;
}

/** Something other than a word that fades with the dissolve. `rest` is the resting value of
    `property`. */
interface Decor {
  element: HTMLElement | SVGElement;
  property: string;
  rest: string;
  left: number;
  right: number;
  edge: number;
  reducedOnly: boolean;
  dissolve: number;
}

/** Turns off a decor element's clicks: `property` is `none` while any of `fades` is complete.
    An element can have more than one fade (a card's title link, for one). */
interface ClickSwitch {
  element: HTMLElement | SVGElement;
  property: ClickProperty;
  fades: Decor[];
  off: boolean;
}

// The styles the dissolve sets on a word.
const WORD_DISSOLVE_STYLES = ["opacity", "top", "left", "pointer-events"] as const;

interface TextEffects {
  /** The portrait's live waves: rings in viewport CSS pixels every frame, then an empty list
      once they end. `ages` are the field's gust ages (wave-ages.ts), or null without the
      field, which means the fallback front. */
  setWaves(rings: readonly WaveRing[], ages: WaveAges | null): void;
  /** The face's bottom edge, on every scroll and layout change. */
  setOutline(outline: PortraitOutline): void;
}

const snap = (value: number): number => Math.round(value / TEXT_STEP) * TEXT_STEP;

/** How far an edge at page y has dissolved under the face's outline, from 0 to 1. `rest` is the
    outline's page y while the portrait scrolls with the page, and `now` is its viewport y right
    now. Text at or above the resting outline never fades, so nothing fades before the
    portrait stops. */
export function dissolveProgress(
  pageY: number,
  rest: number,
  now: number,
  scrollY: number,
): number {
  if (pageY <= rest) return 0;
  const reach = Math.min(DISSOLVE_DISTANCE, pageY - rest);
  const gap = pageY - scrollY - now;
  return snap(clamp01(1 - gap / reach));
}

// Wraps each word of the text in `root` in a span the effects can restyle. Words, not letters:
// some screen readers read letter spans one letter at a time. Hidden copies (`aria-hidden`),
// screen-reader-only text (`.visually-hidden`, which has nothing visible to restyle), and words
// already wrapped are left alone.
function wrapWords(root: HTMLElement): void {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      if (!(node instanceof Text) || !node.data.trim()) return NodeFilter.FILTER_REJECT;
      const parent = node.parentElement;
      // A control's label (like the motion switch's) stays as it is, and so does anything a
      // part marks as its own (`data-fx="none"`: a player's controls, a live region).
      if (
        !parent ||
        parent.closest(".fx-word, [aria-hidden='true'], .visually-hidden, button, [data-fx='none']")
      ) {
        return NodeFilter.FILTER_REJECT;
      }
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  const nodes: Text[] = [];
  while (walker.nextNode()) {
    if (walker.currentNode instanceof Text) nodes.push(walker.currentNode);
  }
  for (const node of nodes) {
    const fragment = document.createDocumentFragment();
    for (const part of node.data.split(/(\s+)/)) {
      if (!part) continue;
      if (/^\s+$/.test(part)) {
        fragment.append(part);
      } else {
        const word = document.createElement("span");
        word.className = "fx-word";
        word.textContent = part;
        fragment.append(word);
      }
    }
    node.replaceWith(fragment);
  }
}

/** Starts both effects on the text in `root`. Words get their wrapper spans here, and again
    whenever the text changes. */
export function startTextEffects(root: HTMLElement): TextEffects {
  let words: Word[] = [];
  let decor: Decor[] = [];
  let clickSwitches: ClickSwitch[] = [];
  let rings: readonly WaveRing[] = [];
  let ages: WaveAges | null = null;
  let outline: PortraitOutline | null = null;
  // The about pages' pictures, which dissolve into dots when motion is on.
  const halftone = new HalftoneLayer(root);

  function clearWord(element: HTMLElement): void {
    for (const name of ["font-weight", "--casl", ...WORD_DISSOLVE_STYLES]) {
      element.style.removeProperty(name);
    }
  }

  // Each element's box in page coordinates and its resting look. A word has no rules of its
  // own, so its parent's computed style is its resting look, even mid-effect. Every style gets
  // cleared before the first read, so the page lays out once, not once per element.
  function measure(): void {
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;
    const spans = [...root.querySelectorAll(".fx-word")].filter(
      (element) => element instanceof HTMLSpanElement,
    );
    for (const element of spans) clearWord(element);
    const faded = DISSOLVE_DECOR.flatMap(
      ({ selector, property, edge, clicks, reducedOnly = false }) =>
        [...root.querySelectorAll(selector)].flatMap((element) => {
          if (!(element instanceof HTMLElement || element instanceof SVGElement)) return [];
          // A shelf's traveling mark (page/shelf-mark.ts) fades with its tile, not by its own
          // position.
          if (element.closest(".portrait, .shelf-mark")) return [];
          // A player's own parts (the play button and its icons) go along with its picture or
          // its rail.
          if (
            element.closest("jrsm-video") &&
            !element.matches("jrsm-video, jrsm-video video, .video-rail")
          )
            return [];
          element.style.removeProperty(property);
          if (clicks) element.style.removeProperty(clicks);
          return [{ element, property, edge, clicks, reducedOnly }];
        }),
    );
    words = spans.map((element, index) => {
      const rect = element.getBoundingClientRect();
      const style = getComputedStyle(element.parentElement ?? root);
      return {
        element,
        left: rect.left + scrollX,
        right: rect.right + scrollX,
        top: rect.top + scrollY,
        middle: rect.top + rect.height / 2 + scrollY,
        weight: Number.parseFloat(style.fontWeight),
        // Recursive's casual axis (`--casl` in site.scss).
        casual: Number.parseFloat(style.getPropertyValue("--casl")) || 0,
        // Looks random, but it's the same on every visit.
        aside: Math.sin(index * 12.9898) % 1,
        height: 0,
        dissolve: 0,
      };
    });
    clickSwitches = [];
    decor = faded.map(({ element, property, edge, clicks, reducedOnly }) => {
      const rect = element.getBoundingClientRect();
      const item: Decor = {
        element,
        property,
        rest: getComputedStyle(element).getPropertyValue(property),
        left: rect.left + scrollX,
        right: rect.right + scrollX,
        edge: rect[edge] + scrollY,
        reducedOnly,
        dissolve: 0,
      };
      if (!clicks) return item;
      const shared = clickSwitches.find(
        (entry) => entry.element === element && entry.property === clicks,
      );
      if (shared) shared.fades.push(item);
      else clickSwitches.push({ element, property: clicks, fades: [item], off: false });
      return item;
    });
    halftone.measure();
    update();
    updateHalftone();
  }

  // The lowest of `bottoms` over a horizontal span of the page, or null where the outline
  // doesn't reach.
  function lowest(
    bottoms: Float32Array,
    left: number,
    right: number,
    scrollX: number,
  ): number | null {
    if (!outline) return null;
    const first = Math.floor((left - scrollX - outline.left) / outline.step);
    const last = Math.floor((right - scrollX - outline.left) / outline.step);
    let bottom: number | null = null;
    for (let i = Math.max(0, first); i <= Math.min(bottoms.length - 1, last); i++) {
      const value = bottoms[i];
      if (value !== undefined) bottom = Math.max(bottom ?? value, value);
    }
    return bottom;
  }

  // How far an edge at page y has dissolved under the outline, from 0 to 1.
  function dissolveAt(
    pageY: number,
    left: number,
    right: number,
    scrollX: number,
    scrollY: number,
  ): number {
    if (!outline) return 0;
    const now = lowest(outline.bottoms, left, right, scrollX);
    const rest = lowest(outline.restBottoms, left, right, scrollX);
    if (now === null || rest === null) return 0;
    return dissolveProgress(pageY, rest, now, scrollY);
  }

  function restyleWord(word: Word): void {
    const { element, height, dissolve } = word;
    if (height === 0 && dissolve === 0) {
      clearWord(element);
      return;
    }
    const crest = Math.max(height, 0);
    const trough = Math.max(-height, 0);
    const thin = smoothstep(0, 1, dissolve / DISSOLVE_THIN);
    const waved = word.weight + TEXT_CREST_WEIGHT * crest - TEXT_TROUGH_WEIGHT * trough;
    const weight = waved + (TEXT_WEIGHT_MIN - waved) * thin;
    const casual = word.casual + (1 - word.casual) * Math.max(crest, thin);
    element.style.fontWeight = String(
      Math.round(Math.min(TEXT_WEIGHT_MAX, Math.max(TEXT_WEIGHT_MIN, weight))),
    );
    element.style.setProperty("--casl", casual.toFixed(2));
    if (dissolve === 0) {
      for (const name of WORD_DISSOLVE_STYLES) element.style.removeProperty(name);
      return;
    }
    const fade = smoothstep(0, 1, (dissolve - DISSOLVE_FADE) / (1 - DISSOLVE_FADE));
    element.style.opacity = (1 - fade).toFixed(2);
    if (dissolve === 1) element.style.pointerEvents = "none";
    else element.style.removeProperty("pointer-events");
    // Reduced motion has no drift, so clear any offset left over from before the switch.
    if (motionReduced()) {
      element.style.removeProperty("top");
      element.style.removeProperty("left");
      return;
    }
    const drift = dissolve * dissolve;
    element.style.top = `${(-DISSOLVE_RISE * drift).toFixed(1)}px`;
    element.style.left = `${(DISSOLVE_SCATTER * word.aside * drift).toFixed(1)}px`;
  }

  function restyleDecor(item: Decor): void {
    const { element, property, rest, dissolve } = item;
    if (item.reducedOnly && !motionReduced()) {
      element.style.removeProperty(property);
      return;
    }
    if (dissolve === 0) {
      element.style.removeProperty(property);
      return;
    }
    const keep = 1 - smoothstep(0, 1, (dissolve - DISSOLVE_FADE) / (1 - DISSOLVE_FADE));
    element.style.setProperty(
      property,
      property === "opacity"
        ? keep.toFixed(2)
        : property === "--dissolve"
          ? (1 - keep).toFixed(2)
          : `color-mix(in srgb, ${rest} ${(keep * 100).toFixed(0)}%, transparent)`,
    );
  }

  // The scroll position comes from the scroll event's copy (page-scroll.ts). A live read after
  // the first word's style write would force a layout.
  function update(): void {
    const scrollX = pageScrollX();
    const scrollY = pageScrollY();
    for (const word of words) {
      let sum = 0;
      for (const ring of rings) {
        const x = (word.left + word.right) / 2 - scrollX;
        const y = word.middle - scrollY;
        let z: number | null;
        if (ages) {
          // The field's gust: the age of the cell under the word, in band thicknesses.
          const age = ageAt(ages, channelOf(ages, ring.id), x, y);
          z = age === null ? null : -age / AGE_BAND;
        } else z = gustOffset(ring, x, y);
        if (z !== null) sum += Math.min(ring.amplitude, 1) * waveShape(z);
      }
      const height = snap(Math.min(1, Math.max(-1, sum)));
      const dissolve = dissolveAt(word.top, word.left, word.right, scrollX, scrollY);
      if (height === word.height && dissolve === word.dissolve) continue;
      word.height = height;
      word.dissolve = dissolve;
      restyleWord(word);
    }
    for (const item of decor) {
      const dissolve = dissolveAt(item.edge, item.left, item.right, scrollX, scrollY);
      if (dissolve === item.dissolve) continue;
      item.dissolve = dissolve;
      restyleDecor(item);
    }
    for (const entry of clickSwitches) {
      const off = entry.fades.some((item) => item.dissolve === 1);
      if (off === entry.off) continue;
      entry.off = off;
      if (off) entry.element.style.setProperty(entry.property, "none");
      else entry.element.style.removeProperty(entry.property);
    }
  }

  // The pictures' dissolve depends on the outline and the scroll, not the waves, so it runs
  // when those change, not on every frame of a wave.
  function updateHalftone(): void {
    if (motionReduced()) halftone.clear();
    else halftone.update(outline, pageScrollX(), pageScrollY());
  }

  // The outline arrives with each scroll event, and its writes wait for that frame's animation
  // callbacks, so the other parts' scroll listeners read the page before any style changes.
  // The waves arrive at the end of the portrait's frame, after its reads, so they write right
  // away. The portrait's clock requests its next frame after its callback, so a frame
  // requested there would run before the next frame's reads.
  let outlineFrame = 0;
  function scheduleOutline(): void {
    if (outlineFrame) return;
    outlineFrame = requestAnimationFrame(() => {
      outlineFrame = 0;
      update();
      updateHalftone();
    });
  }

  // Measures again, at most once a frame, after the layout moves: a resize, the web font
  // arriving, or a bio rewrite (whose new phrase also needs its words wrapped).
  let measureFrame = 0;
  function scheduleMeasure(): void {
    if (measureFrame) return;
    measureFrame = requestAnimationFrame(() => {
      measureFrame = 0;
      measure();
    });
  }
  new MutationObserver((records) => {
    // Changes inside what a part marks as its own (`data-fx="none"`: the halftone layer's
    // canvases, a player's controls as its video plays) or a decoding heading don't touch any
    // text the effects track.
    const ownNode = (node: Node): boolean =>
      node instanceof HTMLElement && node.dataset.fx === "none";
    const inOwnPart = (record: MutationRecord): boolean => {
      const target = record.target instanceof Element ? record.target : record.target.parentElement;
      return target?.closest("[data-fx='none']") != null;
    };
    // A decode (the heading's, or a bio phrase's) changes its boxes every frame. When it ends,
    // it puts the words back, and they get wrapped and measured once (text-decode.ts).
    const inDecode = (record: MutationRecord): boolean => {
      const target = record.target instanceof Element ? record.target : record.target.parentElement;
      return target?.closest("[data-decoding]") != null;
    };
    if (
      records.every(
        (record) =>
          inOwnPart(record) ||
          inDecode(record) ||
          (record.type === "childList" &&
            [...record.addedNodes, ...record.removedNodes].every(ownNode)),
      )
    )
      return;
    wrapWords(root);
    scheduleMeasure();
  }).observe(root, { childList: true, subtree: true, characterData: true });
  window.addEventListener("resize", scheduleMeasure);
  void document.fonts.ready.then(scheduleMeasure);
  // The theme sets the resting colors the dissolve fades from.
  onThemeChange(scheduleMeasure);
  // Reduced motion keeps the fade and drops the drift.
  onMotionChange(() => {
    for (const word of words) restyleWord(word);
    for (const item of decor) restyleDecor(item);
    update();
    updateHalftone();
  });

  wrapWords(root);
  measure();
  return {
    setWaves(next, nextAges) {
      rings = next;
      ages = nextAges;
      update();
    },
    setOutline(next) {
      outline = next;
      scheduleOutline();
    },
  };
}
